import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import CalculationCard from './components/CalculationCard';
import Icon from '../../components/AppIcon';

const SavedCalculations = () => {
  const navigate = useNavigate();
  const [calculations, setCalculations] = useState([]);
  const [filteredCalculations, setFilteredCalculations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadCalculations();
  }, []);

  useEffect(() => {
    filterAndSortCalculations();
  }, [calculations, searchQuery, sortBy]);

  const loadCalculations = () => {
    const saved = JSON.parse(localStorage?.getItem('savedCalculations') || '[]');
    setCalculations(saved);
  };

  const filterAndSortCalculations = () => {
    let filtered = [...calculations];

    if (searchQuery) {
      filtered = filtered?.filter(calc =>
        calc?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        calc?.notes?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        calc?.peptides?.some(p => p?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
      );
    }

    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b?.date) - new Date(a?.date);
        case 'date-asc':
          return new Date(a?.date) - new Date(b?.date);
        case 'title-asc':
          return a?.title?.localeCompare(b?.title);
        case 'title-desc':
          return b?.title?.localeCompare(a?.title);
        default:
          return 0;
      }
    });

    setFilteredCalculations(filtered);
  };

  const handleDelete = (id) => {
    const updated = calculations?.filter(calc => calc?.id !== id);
    setCalculations(updated);
    localStorage?.setItem('savedCalculations', JSON.stringify(updated));
  };

  const handleDuplicate = (calc) => {
    const duplicated = {
      ...calc,
      id: Date.now(),
      title: `${calc?.title} (Copy)`,
      date: new Date()?.toISOString()
    };
    const updated = [duplicated, ...calculations];
    setCalculations(updated);
    localStorage?.setItem('savedCalculations', JSON.stringify(updated));
  };

  const handleBulkDelete = () => {
    const updated = calculations?.filter(calc => !selectedItems?.includes(calc?.id));
    setCalculations(updated);
    localStorage?.setItem('savedCalculations', JSON.stringify(updated));
    setSelectedItems([]);
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(calculations, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `peptide-calculations-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    link?.click();
  };

  const sortOptions = [
    { value: 'date-desc', label: 'Newest First' },
    { value: 'date-asc', label: 'Oldest First' },
    { value: 'title-asc', label: 'Title A-Z' },
    { value: 'title-desc', label: 'Title Z-A' }
  ];

  return (
    <>
      <Helmet>
        <title>Saved Calculations - INF LABS</title>
        <meta name="description" content="View and manage your saved peptide calculations" />
      </Helmet>

      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />
        
        <main className="pt-[100px] pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                  Saved Calculations
                </h1>
                <p className="text-lg text-muted-foreground">
                  {filteredCalculations?.length} calculation{filteredCalculations?.length !== 1 ? 's' : ''} found
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/peptide-calculator')}
                  iconName="Plus"
                >
                  New Calculation
                </Button>
                {calculations?.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleExport}
                    iconName="Download"
                  >
                    Export
                  </Button>
                )}
              </div>
            </div>

            {calculations?.length > 0 && (
              <div className="mb-6 p-4 rounded-xl bg-card border border-border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <Icon
                      name="Search"
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <Input
                      placeholder="Search calculations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select
                    options={sortOptions}
                    value={sortBy}
                    onChange={setSortBy}
                    placeholder="Sort by"
                  />
                </div>

                {selectedItems?.length > 0 && (
                  <div className="mt-4 flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/30">
                    <span className="text-sm font-medium text-foreground">
                      {selectedItems?.length} item{selectedItems?.length !== 1 ? 's' : ''} selected
                    </span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={handleBulkDelete}
                      iconName="Trash2"
                    >
                      Delete Selected
                    </Button>
                  </div>
                )}
              </div>
            )}

            {filteredCalculations?.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-4">
                  <Icon name="Calculator" size={40} className="text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {searchQuery ? 'No calculations found' : 'No saved calculations yet'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery
                    ? 'Try adjusting your search query' :'Start by creating your first peptide calculation'}
                </p>
                {!searchQuery && (
                  <Button
                    onClick={() => navigate('/peptide-calculator')}
                    iconName="Plus"
                    className="bg-gradient-to-r from-primary to-secondary"
                  >
                    Create Calculation
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCalculations?.map((calc) => (
                  <CalculationCard
                    key={calc?.id}
                    calculation={calc}
                    onDelete={handleDelete}
                    onDuplicate={handleDuplicate}
                    isSelected={selectedItems?.includes(calc?.id)}
                    onSelect={(selected) => {
                      if (selected) {
                        setSelectedItems([...selectedItems, calc?.id]);
                      } else {
                        setSelectedItems(selectedItems?.filter(id => id !== calc?.id));
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default SavedCalculations;